import React from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import SEO, { breadcrumbSchema } from "@/components/SEO";

const pillars = [
  {
    n: "I",
    caps: "STRATEGY",
    script: "& Direction",
    dek: "We define where a brand must go — and why. A strategy is not a slide. It is a promise waiting to become behaviour.",
    keywords: ["Vision Architecture", "Positioning", "Cultural Diagnosis", "Growth Design"],
  },
  {
    n: "II",
    caps: "COMMUNICATION",
    script: "& Experience Design",
    dek: "We shape how the brand speaks, moves and appears — from campaign to concierge — so that every touchpoint carries a single, coherent intention.",
    keywords: ["Editorial Voice", "Retail Choreography", "Service Rituals", "Guest Journey"],
  },
  {
    n: "III",
    caps: "OPERATIONAL",
    script: "Mastery",
    dek: "We embed strategy into the daily reality of the business — the standards, the systems, the disciplines that make excellence inevitable.",
    keywords: ["Standards & SOPs", "Quality Rituals", "Performance Culture", "Implementation"],
  },
  {
    n: "IV",
    caps: "LEADERSHIP",
    script: "& Culture",
    dek: "We build the culture, capabilities and human intelligence that outlast any single leader. Exceptionality is transmitted — not installed.",
    keywords: ["Executive Coaching", "Culture Design", "Talent Transmission", "Academy Programs"],
  },
];

export default function Methodology() {
  return (
    <div data-testid="page-methodology" className="pt-32">
      <SEO
        title="Methodology — Humanistic Luxury"
        description="Discover the Humanistic Luxury methodology of Next in Luxury — a four-pillar framework connecting culture, leadership, strategy, experience design and operational mastery."
        path="/methodology"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Methodology", path: "/methodology" },
        ])}
      />
      {/* Title */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="font-eyebrow opacity-60">Chapter N°03</p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <Reveal>
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              — A Registered Method
            </p>
            <h1
              className="title-caps leading-[0.9] mt-8"
              style={{ fontSize: "clamp(3rem, 9vw, 9rem)" }}
            >
              HUMANISTIC
              <br />
              <span className="font-script" style={{ color: "#8C6B3F", textTransform: "none", letterSpacing: 0, fontSize: "1.25em" }}>
                Luxury.
              </span>
              <sup className="title-caps" style={{ fontSize: "0.35em", verticalAlign: "super", marginLeft: "0.15em" }}>™</sup>
            </h1>
            <p className="font-body opacity-80 mt-12 max-w-2xl text-lg leading-relaxed">
              Our methodology connects culture and leadership, strategy,
              experience design and operational mastery — to build luxury that
              is human, intentional, consistent and alive.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Interlude image */}
      <div className="w-full h-[60vh] my-16 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1737920459846-2d0318700658?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxlZGl0b3JpYWwlMjBqb3VybmFsJTIwZGVzayUyMHN0aWxsJTIwbGlmZSUyMGx1eHVyeXxlbnwwfHx8fDE3ODQxNjgxNzN8MA&ixlib=rb-4.1.0&q=85"
          alt="Method"
          className="w-full h-full object-cover duotone"
        />
      </div>

      {/* Pillars */}
      <section
        className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24"
        data-testid="methodology-pillars"
      >
        <div className="flex justify-between items-baseline mb-16">
          <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
            Four Pillars
          </p>
          <p className="font-eyebrow opacity-50">One Discipline</p>
        </div>

        <div>
          {pillars.map((p, i) => (
            <Reveal
              key={p.n}
              delay={i * 100}
              className="grid grid-cols-12 gap-6 py-16"
              // eslint-disable-next-line react/no-unknown-property
            >
              <div className="col-span-12 hairline mb-4" />
              <div className="col-span-2 md:col-span-1">
                <p
                  className="title-caps"
                  style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.4rem)", color: "#8C6B3F", letterSpacing: "0.06em" }}
                >
                  {p.n}
                </p>
              </div>
              <div className="col-span-10 md:col-span-5">
                <h3
                  className="title-caps leading-[0.95]"
                  style={{ fontSize: "clamp(1.85rem, 4vw, 3.5rem)" }}
                >
                  {p.caps}
                  <br />
                  <span
                    className="font-script"
                    style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.35em", color: "#8C6B3F" }}
                  >
                    {p.script}
                  </span>
                </h3>
              </div>
              <div className="col-span-12 md:col-span-6">
                <p className="font-body opacity-80 text-lg leading-relaxed">
                  {p.dek}
                </p>
                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                  {p.keywords.map((k) => (
                    <span key={k} className="font-eyebrow opacity-60">
                      · {k}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Diagram / signature */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6 md:col-start-4">
          <Reveal>
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              The Signature
            </p>
            <p
              className="font-serif-italic mt-8 leading-[1.1]"
              style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.8rem)" }}
            >
              Soul, so the brand has meaning.
              <br />
              System, so meaning can be lived.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Continue */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 flex justify-between items-end">
        <div>
          <p className="font-eyebrow opacity-60">Continue</p>
          <h2 className="font-serif-display mt-4" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            The <span className="font-serif-italic">Expert Minds</span>
          </h2>
        </div>
        <Link to="/expert-minds" className="btn-ghost" data-testid="methodology-continue-link">
          Chapter N°04 <span className="arrow">→</span>
        </Link>
      </section>
    </div>
  );
}
